import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchThreadsByFolder as apiFetchThreads } from '../services/graphMailService';

// ---------------------------------------------------------------------------
// localStorage pin helpers
// ---------------------------------------------------------------------------

const LS_KEY = 'bpa_outlook_pins';

/** Returns { [conversationId]: pinnedAtMs } from localStorage, or {} on error. */
function loadPinMap() {
  try { return JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { return {}; }
}

/** Merge pin data from localStorage into an array of thread objects (mutates in place). */
function hydratePins(threads) {
  const pinMap = loadPinMap();
  for (const t of threads) {
    const pinnedAt = pinMap[t.conversationId];
    t.isPinned = pinnedAt !== undefined;
    t.pinnedAt = pinnedAt ?? null;
  }
}

// Imported lazily to avoid circular deps — only the action type string is needed
const LOAD_FOLDER_FULFILLED      = 'graphMail/loadFolderThreads/fulfilled';
const LOAD_MORE_FOLDER_PENDING   = 'graphMail/loadMoreFolderThreads/pending';
const LOAD_MORE_FOLDER_FULFILLED = 'graphMail/loadMoreFolderThreads/fulfilled';
const LOAD_MORE_FOLDER_REJECTED  = 'graphMail/loadMoreFolderThreads/rejected';

// ---------------------------------------------------------------------------
// Async Thunks
// ---------------------------------------------------------------------------

export const fetchThreadsByFolder = createAsyncThunk(
  'mailThreads/fetchByFolder',
  async ({ folderId, cursor = null }, { rejectWithValue }) => {
    try {
      const data = await apiFetchThreads(folderId, { cursor, top: 50 });
      return { folderId, ...data };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchMoreThreads = createAsyncThunk(
  'mailThreads/fetchMore',
  async ({ folderId, cursor }, { rejectWithValue }) => {
    try {
      const data = await apiFetchThreads(folderId, { cursor, top: 50 });
      return { folderId, ...data };
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ---------------------------------------------------------------------------
// Initial State
// ---------------------------------------------------------------------------

const initialState = {
  // Normalized thread entities: { [conversationId]: Thread }
  byId: {},

  // Per-folder ordered list of conversationIds: { [folderId]: string[] }
  idsByFolder: {},

  // Per-folder pagination: { [folderId]: { cursor, hasMore, isFetchingMore } }
  pagination: {},

  // Per-folder load status: { [folderId]: 'idle' | 'loading' | 'ready' | 'error' }
  status: {},

  // Per-folder error: { [folderId]: string | null }
  errors: {},
};

// ---------------------------------------------------------------------------
// Thread shape reference:
// {
//   conversationId: string,
//   subject: string,
//   latestReceivedAt: string (ISO),
//   latestSenderId: string,
//   latestSenderName: string,
//   participants: string[],
//   hasUnread: boolean,
//   unreadCount: number,
//   messageCount: number,
//   folderId: string,
//   isFlagged: boolean,
//   categories: string[],
//   bodyPreview: string,
//   messageIds: string[],
// }
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

const mailThreadsSlice = createSlice({
  name: 'mailThreads',
  initialState,
  reducers: {
    // -----------------------------------------------------------------------
    // Selection & expansion
    // -----------------------------------------------------------------------

    // Upsert a single thread (from WebSocket delta event)
    upsertThread(state, action) {
      const thread = action.payload;
      const { conversationId, folderId } = thread;
      const existing = state.byId[conversationId] || {};

      state.byId[conversationId] = {
        ...existing,
        ...thread,
        // Delta sync never carries pin data — preserve what Redux already has
        isPinned: existing.isPinned || false,
        pinnedAt: existing.pinnedAt || null,
      };

      if (!state.idsByFolder[folderId]) {
        state.idsByFolder[folderId] = [];
      }
      if (!state.idsByFolder[folderId].includes(conversationId)) {
        state.idsByFolder[folderId].unshift(conversationId);
      }
    },

    // Remove a thread (delta: deleted or moved out)
    removeThread(state, action) {
      const { conversationId, folderId } = action.payload;
      delete state.byId[conversationId];
      if (state.idsByFolder[folderId]) {
        state.idsByFolder[folderId] = state.idsByFolder[folderId].filter(
          (id) => id !== conversationId
        );
      }
    },

    // Mark all messages in a thread as read (optimistic)
    markThreadRead(state, action) {
      const { conversationId } = action.payload;
      if (state.byId[conversationId]) {
        state.byId[conversationId].hasUnread = false;
        state.byId[conversationId].unreadCount = 0;
      }
    },

    // Mark thread unread (optimistic)
    markThreadUnread(state, action) {
      const { conversationId } = action.payload;
      if (state.byId[conversationId]) {
        state.byId[conversationId].hasUnread = true;
        state.byId[conversationId].unreadCount =
          (state.byId[conversationId].unreadCount || 0) + 1;
      }
    },

    // Flag / unflag thread (optimistic)
    setThreadFlagged(state, action) {
      const { conversationId, isFlagged } = action.payload;
      if (state.byId[conversationId]) {
        state.byId[conversationId].isFlagged = isFlagged;
      }
    },

    // Move thread to different folder (optimistic)
    moveThread(state, action) {
      const { conversationId, fromFolderId, toFolderId } = action.payload;
      if (state.idsByFolder[fromFolderId]) {
        state.idsByFolder[fromFolderId] = state.idsByFolder[fromFolderId].filter(
          (id) => id !== conversationId
        );
      }
      if (!state.idsByFolder[toFolderId]) {
        state.idsByFolder[toFolderId] = [];
      }
      state.idsByFolder[toFolderId].unshift(conversationId);
      if (state.byId[conversationId]) {
        state.byId[conversationId].folderId = toFolderId;
      }
    },

    // Pin / unpin thread — persisted to localStorage so it survives refresh
    setPinned(state, action) {
      const { conversationId, isPinned } = action.payload;
      if (!state.byId[conversationId]) return;

      const pinnedAt = isPinned ? Date.now() : null;
      state.byId[conversationId].isPinned = isPinned;
      state.byId[conversationId].pinnedAt = pinnedAt;

      // Write through to localStorage
      try {
        const stored = JSON.parse(localStorage.getItem('bpa_outlook_pins') || '{}');
        if (isPinned) {
          stored[conversationId] = pinnedAt;
        } else {
          delete stored[conversationId];
        }
        localStorage.setItem('bpa_outlook_pins', JSON.stringify(stored));
      } catch { /* localStorage unavailable — pin works for this session only */ }
    },

    // Delete thread from folder (optimistic)
    deleteThread(state, action) {
      const { conversationId, folderId } = action.payload;
      if (state.idsByFolder[folderId]) {
        state.idsByFolder[folderId] = state.idsByFolder[folderId].filter(
          (id) => id !== conversationId
        );
      }
      delete state.byId[conversationId];
    },

    // Update latest preview after send
    updateThreadPreview(state, action) {
      const { conversationId, bodyPreview, latestReceivedAt, latestSenderName } = action.payload;
      if (state.byId[conversationId]) {
        state.byId[conversationId].bodyPreview = bodyPreview;
        state.byId[conversationId].latestReceivedAt = latestReceivedAt;
        state.byId[conversationId].latestSenderName = latestSenderName;
      }
    },

    // Clear all threads for a folder (before re-fetch)
    clearFolderThreads(state, action) {
      const { folderId } = action.payload;
      const ids = state.idsByFolder[folderId] || [];
      ids.forEach((id) => {
        delete state.byId[id];
      });
      delete state.idsByFolder[folderId];
      delete state.pagination[folderId];
      state.status[folderId] = 'idle';
    },
  },

  extraReducers: (builder) => {
    // ------------------------------------------------------------------
    // fetchThreadsByFolder
    // ------------------------------------------------------------------
    builder
      .addCase(fetchThreadsByFolder.pending, (state, action) => {
        const { folderId } = action.meta.arg;
        state.status[folderId] = 'loading';
        state.errors[folderId] = null;
      })
      .addCase(fetchThreadsByFolder.fulfilled, (state, action) => {
        const { folderId, threads, messages, cursor, hasMore } = action.payload;

        hydratePins(threads);
        threads.forEach((thread) => {
          state.byId[thread.conversationId] = thread;
        });

        state.idsByFolder[folderId] = threads.map((t) => t.conversationId);
        state.pagination[folderId] = { cursor, hasMore, isFetchingMore: false };
        state.status[folderId] = 'ready';

        // Note: messages are handled by messagesSlice via the same action
        // Components should also dispatch upsertMessages with action.payload.messages
      })
      .addCase(fetchThreadsByFolder.rejected, (state, action) => {
        const { folderId } = action.meta.arg;
        state.status[folderId] = 'error';
        state.errors[folderId] = action.payload || 'Failed to load threads';
      });

    // ------------------------------------------------------------------
    // fetchMoreThreads (pagination append)
    // ------------------------------------------------------------------
    builder
      .addCase(fetchMoreThreads.pending, (state, action) => {
        const { folderId } = action.meta.arg;
        if (!state.pagination[folderId]) state.pagination[folderId] = { cursor: null, hasMore: false, isFetchingMore: false };
        state.pagination[folderId].isFetchingMore = true;
      })
      .addCase(fetchMoreThreads.fulfilled, (state, action) => {
        const { folderId, threads, cursor, hasMore } = action.payload;

        threads.forEach((thread) => {
          state.byId[thread.conversationId] = thread;
        });

        const newIds = threads.map((t) => t.conversationId);
        if (!state.idsByFolder[folderId]) state.idsByFolder[folderId] = [];
        const existingSet = new Set(state.idsByFolder[folderId]);
        newIds.forEach((id) => {
          if (!existingSet.has(id)) state.idsByFolder[folderId].push(id);
        });

        state.pagination[folderId] = { cursor, hasMore, isFetchingMore: false };
      })
      .addCase(fetchMoreThreads.rejected, (state, action) => {
        const { folderId } = action.meta.arg;
        if (state.pagination[folderId]) {
          state.pagination[folderId].isFetchingMore = false;
        }
      });

    // ------------------------------------------------------------------
    // graphMail/loadFolderThreads — initial load from bootstrap/folder switch
    // Sets pagination so EmailList infinite scroll works
    // ------------------------------------------------------------------
    builder
      .addMatcher(
        (action) => action.type === LOAD_FOLDER_FULFILLED,
        (state, action) => {
          const { folderId, threads, cursor, hasMore } = action.payload;
          if (!folderId) return;
          // Replace folder thread list (initial load)
          if (threads?.length) {
            hydratePins(threads);
            threads.forEach((t) => { state.byId[t.conversationId] = t; });
            state.idsByFolder[folderId] = threads.map((t) => t.conversationId);
          }
          state.status[folderId] = 'ready';
          state.pagination[folderId] = { cursor: cursor || null, hasMore: !!hasMore, isFetchingMore: false };
        }
      )
      .addMatcher(
        (action) => action.type === LOAD_MORE_FOLDER_PENDING,
        (state, action) => {
          const folderId = action.meta?.arg?.folderId;
          if (!folderId) return;
          if (!state.pagination[folderId]) state.pagination[folderId] = { cursor: null, hasMore: false, isFetchingMore: false };
          state.pagination[folderId].isFetchingMore = true;
        }
      )
      .addMatcher(
        (action) => action.type === LOAD_MORE_FOLDER_REJECTED,
        (state, action) => {
          const folderId = action.meta?.arg?.folderId;
          if (folderId && state.pagination[folderId]) state.pagination[folderId].isFetchingMore = false;
        }
      )
      .addMatcher(
        (action) => action.type === LOAD_MORE_FOLDER_FULFILLED,
        (state, action) => {
          const { folderId, threads, cursor, hasMore } = action.payload;
          if (!folderId) return;
          // Append new threads to folder list
          if (threads?.length) {
            const existing = new Set(state.idsByFolder[folderId] || []);
            threads.forEach((t) => {
              state.byId[t.conversationId] = t;
              if (!existing.has(t.conversationId)) {
                if (!state.idsByFolder[folderId]) state.idsByFolder[folderId] = [];
                state.idsByFolder[folderId].push(t.conversationId);
              }
            });
          }
          state.pagination[folderId] = { cursor: cursor || null, hasMore: !!hasMore, isFetchingMore: false };
        }
      );
  },
});

export const {
  upsertThread,
  removeThread,
  markThreadRead,
  markThreadUnread,
  setThreadFlagged,
  setPinned,
  moveThread,
  deleteThread,
  updateThreadPreview,
  clearFolderThreads,
} = mailThreadsSlice.actions;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export const selectThreadById = (state, conversationId) =>
  state.mailThreads.byId[conversationId];

export const selectThreadIdsByFolder = (state, folderId) =>
  state.mailThreads.idsByFolder[folderId] || [];

export const selectThreadsByFolder = (state, folderId) => {
  const ids = state.mailThreads.idsByFolder[folderId] || [];
  return ids.map((id) => state.mailThreads.byId[id]).filter(Boolean);
};

export const selectFolderLoadStatus = (state, folderId) =>
  state.mailThreads.status[folderId] || 'idle';

export const selectFolderPagination = (state, folderId) =>
  state.mailThreads.pagination[folderId] || { cursor: null, hasMore: false, isFetchingMore: false };

export default mailThreadsSlice.reducer;
